import React, { useState } from 'react';
import toaster from 'react-hot-toast';
import { useQuery } from '@tanstack/react-query';

// components
import MachineStatusSelection from 'components/admin/deviceDashboard/deviceEditor/simulator/status/MachineStatusSelection';
import CycleSelection from 'components/admin/deviceDashboard/deviceEditor/simulator/status/CycleSelection';
import ModifierSelection from 'components/admin/deviceDashboard/deviceEditor/simulator/status/ModifierSelection';
import DeviceStatusFinalReviewScreen from 'components/admin/deviceDashboard/deviceEditor/simulator/status/DeviceStatusFinalReviewScreen';

// APIs
import {
  fetchAvailableCyclesForDevice,
  fetchAvailableModifiersForDevice,
} from 'api/admin/devices/devices';

const DeviceStatusSimulator = (props) => {
  const { device, onBack, onSubmit } = props;
  const deviceStatusScreens = {
    STATUS_SELECTION: 'STATUS_SELECTION',
    CYCLE_SELECTION: 'CYCLE_SELECTION',
    MODIFIER_SELECTION: 'MODIFIER_SELECTION',
    FINAL_REVIEW: 'FINAL_REVIEW',
  };

  // form data
  const [wizardStep, setWizardStep] = useState(deviceStatusScreens.STATUS_SELECTION);
  const [selectedMachineStatus, setSelectedMachineStatus] = useState(null);
  const [selectedCycleHex, setSelectedCycleHex] = useState(null);
  const [selectedCycleName, setSelectedCycleName] = useState(null);
  const [selectedModifierHex, setSelectedModifierHex] = useState(null);
  const [selectedModifierName, setSelectedModifierName] = useState(null);

  /**
   * Fetch the cycle details for the given device
   */
  const { data: cycleDetails } = useQuery(
    ['fetchAvailableCyclesForDevice', { id: device.id }],
    async () => {
      const { data } = await fetchAvailableCyclesForDevice(device.id);
      return data.cycleDetails;
    },
    {
      onError: (error) => {
        toaster(
          `Error in fetching cycles for machine/device pairing: ${error?.response?.data?.error}`,
        );
      },
    },
  );

  /**
   * Fetch the cycle details for the given device
   */
  const { data: modifierDetails } = useQuery(
    ['fetchAvailableModifiersForDevice', { id: device.id }],
    async () => {
      const { data } = await fetchAvailableModifiersForDevice(device.id);
      return data.modifierDetails;
    },
    {
      onError: (error) => {
        toaster(
          `Error in fetching modifiers for machine/device pairing: ${error?.response?.data?.error}`,
        );
      },
    },
  );

  /**
   * send formatted data from the wizard steps to the parent to complete
   * the "run machine" and device transaction simulation
   */
  const submitDeviceStatusSimulationData = () => {
    const dataToSubmit = {
      deviceId: device.id,
      machineId: device.machineId,
      status: selectedMachineStatus,
      cycleHex: selectedCycleHex,
      modifierHex: selectedModifierHex,
    };

    return onSubmit(dataToSubmit);
  };

  /**
   * navigate backwards in the wizard based on current progress in the wizard
   */
  const handleOnBackFunctionality = () => {
    switch (wizardStep) {
      case deviceStatusScreens.STATUS_SELECTION:
        return onBack();
      case deviceStatusScreens.CYCLE_SELECTION:
        setSelectedMachineStatus(null);
        setSelectedCycleHex(null);
        setSelectedCycleName(null);
        return setWizardStep(deviceStatusScreens.STATUS_SELECTION);
      case deviceStatusScreens.MODIFIER_SELECTION:
        setSelectedCycleHex(null);
        setSelectedCycleName(null);
        setSelectedModifierHex(null);
        setSelectedModifierName(null);
        return setWizardStep(deviceStatusScreens.CYCLE_SELECTION);
      case deviceStatusScreens.FINAL_REVIEW:
        if (cycleDetails) {
          setSelectedCycleHex(null);
          setSelectedCycleName(null);
          return setWizardStep(deviceStatusScreens.CYCLE_SELECTION);
        } else {
          setSelectedMachineStatus(null);
          return setWizardStep(deviceStatusScreens.STATUS_SELECTION);
        }
      default:
        return onBack();
    }
  };

  /**
   * render the final review screen for user to review their submissions
   */
  const renderFinalReviewScreen = () => {
    return (
      <DeviceStatusFinalReviewScreen
        deviceId={device.id}
        machineId={device.machineId}
        selectedMachineStatus={selectedMachineStatus}
        onBack={handleOnBackFunctionality}
        onNext={submitDeviceStatusSimulationData}
        disableFinalSubmitButton={() => {
          return !selectedMachineStatus;
        }}
        selectedCycleName={selectedCycleName}
        selectedModifierName={selectedModifierName}
      />
    );
  };

  /**
   * Render the UI to select status
   */
  const renderStatusSelection = () => {
    return (
      <MachineStatusSelection
        selectedMachineStatus={selectedMachineStatus}
        onBack={handleOnBackFunctionality}
        onNext={handleNextForStatusSelection}
        onValueChange={setSelectedMachineStatus}
      />
    );
  };

  /**
   * If cycleDetails are available, let the user select their cycle
   * If not, move them forward to the final review screen
   */
  const handleNextForStatusSelection = () => {
    if (cycleDetails.length) {
      setWizardStep(deviceStatusScreens.CYCLE_SELECTION);
    } else {
      setWizardStep(deviceStatusScreens.FINAL_REVIEW);
    }
  };

  /**
   * Set the cycle details in state
   *
   * @param {Object} cycleDetails
   */
  const handleCycleSelection = (cycleDetails) => {
    setSelectedCycleHex(cycleDetails.hexValue);
    setSelectedCycleName(cycleDetails.label);
  };

  /**
   * render the UI to select cycles
   */
  const renderCycleSelection = () => {
    return (
      <CycleSelection
        selectedCycleName={selectedCycleName}
        cycles={cycleDetails}
        onBack={handleOnBackFunctionality}
        onNext={() => {
          if (modifierDetails.length) {
            setWizardStep(deviceStatusScreens.MODIFIER_SELECTION);
          } else {
            setWizardStep(deviceStatusScreens.FINAL_REVIEW);
          }
        }}
        onValueChange={handleCycleSelection}
      />
    );
  };

  /**
   * Set the modifier details in state
   *
   * @param {Object} modifierDetails
   */
  const handleModifierSelection = (modifierDetails) => {
    setSelectedModifierHex(modifierDetails.hexValue);
    setSelectedModifierName(modifierDetails.label);
  };

  /**
   * render the UI to select cycles
   */
  const renderModifierSelection = () => {
    return (
      <ModifierSelection
        selectedModifierName={selectedModifierName}
        modifiers={modifierDetails}
        onBack={handleOnBackFunctionality}
        onNext={() => {
          setWizardStep(deviceStatusScreens.FINAL_REVIEW);
        }}
        onValueChange={handleModifierSelection}
      />
    );
  };

  /**
   * Determine the proper form/wizard step based on state of the component
   */
  const renderDeviceStatusWizardSteps = () => {
    switch (wizardStep) {
      case deviceStatusScreens.STATUS_SELECTION:
        return renderStatusSelection();
      case deviceStatusScreens.CYCLE_SELECTION:
        return renderCycleSelection();
      case deviceStatusScreens.MODIFIER_SELECTION:
        return renderModifierSelection();
      case deviceStatusScreens.FINAL_REVIEW:
        return renderFinalReviewScreen();
      default:
        return renderStatusSelection();
    }
  };

  return <>{renderDeviceStatusWizardSteps()}</>;
};

export default DeviceStatusSimulator;
